import React, { useMemo, useState } from 'react';
import Grid2 from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SecurityIcon from '@mui/icons-material/Security';
import { useTheme, alpha } from '@mui/material/styles';
import { ScopeCostRow } from '../Model/CostType';
import {
  fmtCompact,
  NOK,
  CurrencyConfig,
} from '../../../Components/Layout/SqlServer/Common/util';
import {
  ResponsiveContainer,
  Cell,
  PieChart,
  Tooltip,
  Pie,
  Sector,
} from '../../../lib/RechartsWrapper';
import { SCOPE_COLORS } from '../Model/Constants';

interface ScopeTabProps {
  scopeSummary: ScopeCostRow[];
  currency?: CurrencyConfig;
}

interface InnerRingItem {
  Scope: string;
  Cost: number;
}

const ScopeTab: React.FC<ScopeTabProps> = ({
  scopeSummary,
  currency = NOK,
}) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeType, setActiveType] = useState<'inner' | 'outer' | null>(null);

  // ── Data Transformation ───────────────────────────────────────────────────

  const innerData = useMemo<InnerRingItem[]>(() => {
    const map = new Map<string, InnerRingItem>();
    for (const row of scopeSummary) {
      const existing = map.get(row.Scope) || { Scope: row.Scope, Cost: 0 };
      existing.Cost += row.Cost;
      map.set(row.Scope, existing);
    }
    return Array.from(map.values()).sort((a, b) => b.Cost - a.Cost);
  }, [scopeSummary]);

  const outerData = useMemo(() => {
    return [...scopeSummary].sort((a, b) => {
      if (a.Scope !== b.Scope) {
        const aIdx = innerData.findIndex((i) => i.Scope === a.Scope);
        const bIdx = innerData.findIndex((i) => i.Scope === b.Scope);
        return aIdx - bIdx;
      }
      return b.Cost - a.Cost;
    });
  }, [scopeSummary, innerData]);

  const total = innerData.reduce((s, r) => s + r.Cost, 0);

  const activeItem = useMemo<InnerRingItem | ScopeCostRow | null>(() => {
    if (activeType === 'inner' && activeIndex !== null)
      return innerData[activeIndex];
    if (activeType === 'outer' && activeIndex !== null)
      return outerData[activeIndex];
    return null;
  }, [activeType, activeIndex, innerData, outerData]);

  // ── Hover Focus Mode Logic ───────────────────────────────────────────────

  const isScopeFocused = (scope: string) => {
    if (!activeItem) return true;
    return activeItem.Scope === scope;
  };

  // ── Render Helpers ────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderActiveShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
      props;
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          style={{
            filter: `drop-shadow(0 0 12px ${alpha(fill, 0.7)})`,
            transition: 'all 0.3s ease',
          }}
        />
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={outerRadius + 12}
          outerRadius={outerRadius + 14}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  const sectionLabel = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.12em',
    color: 'text.secondary',
    mb: 2,
  };

  return (
    <Grid2 container spacing={3}>
      {/* ── Sunburst Chart + Metrics Card ────────────────────────────────────── */}
      <Grid2 xs={12} md={5}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          <Typography sx={sectionLabel}>Operational Hierarchy</Typography>

          <Box sx={{ flex: 1, position: 'relative', minHeight: 340 }}>
            <ResponsiveContainer width="100%" height={340}>
              <PieChart>
                {/* Inner Ring: Scopes */}
                <Pie
                  data={innerData}
                  dataKey="Cost"
                  nameKey="Scope"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  stroke="none"
                  activeIndex={
                    activeType === 'inner'
                      ? activeIndex ?? undefined
                      : undefined
                  }
                  activeShape={renderActiveShape}
                  onMouseEnter={(data: unknown, index: number) => {
                    setActiveIndex(index);
                    setActiveType('inner');
                  }}
                  onMouseLeave={() => {
                    setActiveIndex(null);
                    setActiveType(null);
                  }}
                >
                  {innerData.map((s) => (
                    <Cell
                      key={s.Scope}
                      fill={
                        SCOPE_COLORS[s.Scope] ?? theme.palette.text.disabled
                      }
                      style={{ transition: 'all 0.3s ease' }}
                    />
                  ))}
                </Pie>

                {/* Outer Ring: Layers */}
                <Pie
                  data={outerData}
                  dataKey="Cost"
                  nameKey="Layer"
                  cx="50%"
                  cy="50%"
                  innerRadius={100}
                  outerRadius={125}
                  paddingAngle={1}
                  stroke="none"
                  activeIndex={
                    activeType === 'outer'
                      ? activeIndex ?? undefined
                      : undefined
                  }
                  activeShape={renderActiveShape}
                  onMouseEnter={(data: unknown, index: number) => {
                    setActiveIndex(index);
                    setActiveType('outer');
                  }}
                  onMouseLeave={() => {
                    setActiveIndex(null);
                    setActiveType(null);
                  }}
                >
                  {outerData.map((s) => (
                    <Cell
                      key={`${s.Scope}-${s.Layer}`}
                      fill={alpha(
                        SCOPE_COLORS[s.Scope] ?? theme.palette.text.disabled,
                        0.7,
                      )}
                      style={{ transition: 'all 0.3s ease' }}
                    />
                  ))}
                </Pie>
                <Tooltip content={<></>} />
              </PieChart>
            </ResponsiveContainer>

            {/* Dynamic Center Panel */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                width: 110,
              }}
            >
              <Typography
                sx={{
                  fontSize: activeItem ? 11 : 18,
                  fontWeight: 700,
                  color: activeItem ? 'text.secondary' : 'text.primary',
                  lineHeight: 1,
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  textTransform: 'uppercase',
                }}
              >
                {(() => {
                  if (!activeItem) return fmtCompact(total, currency);
                  if ('Layer' in activeItem) return activeItem.Layer;
                  return activeItem.Scope;
                })()}
              </Typography>
              <Typography
                sx={{
                  fontSize: activeItem ? 16 : 10,
                  fontWeight: activeItem ? 700 : 400,
                  color: activeItem ? 'text.primary' : 'text.disabled',
                  transition: 'all 0.2s ease',
                }}
              >
                {activeItem
                  ? fmtCompact(activeItem.Cost, currency)
                  : 'Total Period'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid2>

      {/* ── Granular Breakdown List ────────────────────────────────────────── */}
      <Grid2 xs={12} md={7}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            height: '100%',
            bgcolor: 'background.paper',
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography sx={{ ...sectionLabel, mb: 0 }}>
              Distribution Analysis
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon sx={{ fontSize: 14, color: 'success.main' }} />
              <Typography variant="caption" color="text.disabled">
                Verified Tag v3
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {innerData.map((scopeGroup) => {
              const color =
                SCOPE_COLORS[scopeGroup.Scope] ?? theme.palette.text.disabled;
              const layers = outerData.filter(
                (l) => l.Scope === scopeGroup.Scope,
              );
              const scopePct = (scopeGroup.Cost / total) * 100;
              const isFocused = isScopeFocused(scopeGroup.Scope);

              return (
                <Box
                  key={scopeGroup.Scope}
                  sx={{
                    mb: 1,
                    p: 2,
                    borderRadius: 3,
                    border:
                      isFocused && activeItem
                        ? `1px solid ${alpha(color, 0.3)}`
                        : '1px solid transparent',
                    bgcolor:
                      isFocused && activeItem
                        ? alpha(color, 0.05)
                        : 'transparent',
                    opacity: isFocused ? 1 : 0.3,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: alpha(color, 0.04),
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: color,
                        boxShadow: `0 0 8px ${alpha(color, 0.4)}`,
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, flex: 1, letterSpacing: '0.01em' }}
                    >
                      {scopeGroup.Scope}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 800,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {fmtCompact(scopeGroup.Cost, currency)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 45,
                        textAlign: 'right',
                        fontWeight: 800,
                        color: 'text.secondary',
                      }}
                    >
                      {scopePct.toFixed(1)}%
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      pl: 3,
                    }}
                  >
                    {layers.map((layer) => {
                      const up = (layer.DeltaPercent ?? 0) >= 0;
                      return (
                        <Box
                          key={layer.Layer}
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              color: 'text.secondary',
                              fontWeight: 600,
                              fontSize: 11,
                            }}
                          >
                            {layer.Layer}
                          </Typography>
                          <Box sx={{ width: 100 }}>
                            <LinearProgress
                              variant="determinate"
                              value={layer.Percent}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                bgcolor: alpha(color, 0.08),
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 2,
                                  bgcolor: alpha(color, 0.8),
                                },
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              minWidth: 55,
                              justifyContent: 'flex-end',
                            }}
                          >
                            {layer.DeltaPercent !== null && (
                              <>
                                {up ? (
                                  <TrendingUpIcon
                                    sx={{ fontSize: 12 }}
                                    color="error"
                                  />
                                ) : (
                                  <TrendingDownIcon
                                    sx={{ fontSize: 12 }}
                                    color="success"
                                  />
                                )}
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: up ? 'error.main' : 'success.main',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {Math.abs(layer.DeltaPercent ?? 0).toFixed(1)}
                                  %
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Grid2>
    </Grid2>
  );
};

export default ScopeTab;
